import { useCallback, useEffect, useState } from 'react';
import {
  currentLang,
  displayMode as readDisplayMode,
  isRTL,
  reattachListeners,
  retranslateAll,
  setDisplayMode,
  setLanguage,
} from '../lib/i18n.js';

// React-side mirror of the global i18n state.
//
// window.i18nConfig remains the source of truth — it is shared with the lecture
// pages and persists the choice. This hook holds a copy so the nav re-renders
// when it changes, and drives the two side effects the old page ran inline: a
// full re-translation pass after a language change, and reattaching the hover
// listeners after a display-mode change.
export function useI18n() {
  const [lang, setLang] = useState(() => currentLang());
  const [mode, setMode] = useState(() => readDisplayMode());
  // A reader who lands on a translated page should be told it is machine
  // translated before they read it, so a non-English initial language opens the
  // notice straight away — same as the pre-React page did on load.
  const [disclaimerOpen, setDisclaimerOpen] = useState(() => currentLang() !== 'en');

  useEffect(() => {
    if (isRTL()) {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.removeAttribute('dir');
    }
  }, [lang]);

  const chooseLang = useCallback((next) => {
    setLanguage(next);
    setLang(next);
    retranslateAll();
    if (next !== 'en') setDisclaimerOpen(true);
  }, []);

  const chooseMode = useCallback((next) => {
    setDisplayMode(next);
    setMode(next);
    reattachListeners();
  }, []);

  const openDisclaimer = useCallback(() => setDisclaimerOpen(true), []);
  const closeDisclaimer = useCallback(() => setDisclaimerOpen(false), []);

  return {
    lang,
    mode,
    chooseLang,
    chooseMode,
    disclaimerOpen,
    openDisclaimer,
    closeDisclaimer,
    // The nav's disclaimer button only exists while something is translated.
    disclaimerVisible: lang !== 'en',
  };
}
